import { useCallback, useRef, useState } from 'react';

/**
 * « controllerchange » n'arrive pas toujours — un worker qui refuse de céder la main, un
 * navigateur qui ne l'émet jamais. Passé ce délai on recharge quand même : mieux vaut une
 * page servie depuis le cache qu'une icône qui tourne dans le vide.
 */
const CONTROLLER_CHANGE_TIMEOUT_MS = 3000;

/**
 * Recharger l'application en récupérant réellement la dernière version déployée.
 *
 * Un `location.reload()` seul ne suffit pas : la coquille est précachée par le service
 * worker, et un rechargement classique la resert depuis ce cache. Il faut d'abord demander
 * au worker de chercher une nouvelle version, puis lui faire prendre la main — c'est le
 * chemin qu'emprunte déjà le bouton « Recharger » de la bannière de mise à jour.
 *
 * On passe par l'API standard `navigator.serviceWorker` plutôt que par `useRegisterSW()` :
 * ce hook est déjà appelé par UpdateBanner, et un second appel réenregistrerait le worker.
 *
 * Rien ne se déclenche tout seul ici. Le rechargement reste un geste explicite du membre,
 * comme l'exige le commentaire de `sw.ts` : l'appliquer pendant une saisie de note, en
 * pleine séance, ferait perdre son travail.
 */
export function useAppReload(): { reload: () => Promise<void>; busy: boolean } {
  const [busy, setBusy] = useState(false);
  // Un `ref` et pas seulement `busy` : deux clics rapprochés se suivent dans le même rendu,
  // et l'état React n'aurait pas encore basculé au second.
  const running = useRef(false);

  const reload = useCallback(async (): Promise<void> => {
    if (running.current) return;
    running.current = true;
    setBusy(true);

    const waiting = await findWaitingWorker();
    if (!waiting) {
      window.location.reload();
      return;
    }

    reloadOnControllerChange();
    waiting.postMessage({ type: 'SKIP_WAITING' });
  }, []);

  return { reload, busy };
}

/**
 * Le worker prêt à prendre la main, s'il y en a un.
 *
 * `navigator.serviceWorker` est absent en contexte non sécurisé et sous jsdom, et `update()`
 * échoue simplement quand le réseau est coupé — dans les deux cas on renvoie `null`, ce qui
 * ramène l'appelant au rechargement ordinaire.
 */
async function findWaitingWorker(): Promise<ServiceWorker | null> {
  if (!('serviceWorker' in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return null;
    await registration.update();
    return registration.waiting;
  } catch {
    return null;
  }
}

/** Recharge dès que le nouveau worker contrôle la page — une fois, et pas deux. */
function reloadOnControllerChange(): void {
  let reloaded = false;
  const once = (): void => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  // Chrome émet « controllerchange » deux fois dans certains scénarios de mise à jour.
  navigator.serviceWorker.addEventListener('controllerchange', once, { once: true });
  window.setTimeout(once, CONTROLLER_CHANGE_TIMEOUT_MS);
}
